<script setup>
import { computed, inject, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { distanceText, statusLabel, statusTone, styleVars, wetnessLine } from './shared.js';
import { trailCounts } from './trailCounts.js';
import ThConditionsForecast from './ThConditionsForecast.vue';
import { conditionsForecast, forecastFor, forecastSlots } from '../forecastSlots.js';
import { conditionRowHref } from '../trailsPage.js';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

// Classic's two keys for the same thing: network_ids predates
// item_ids and old rows still carry it.
const mode = computed(() => (props.config.mode === 'trails' ? 'trails' : 'networks'));
const eyebrow = computed(() => String(props.config.label ?? '').trim());
const heading = computed(() => String(props.config.heading ?? '').trim());

const rows = computed(() => {
    const all = (mode.value === 'trails' ? props.site_data?.trails : props.site_data?.networks) || [];
    const ids = (props.config.item_ids || props.config.network_ids || []).map(Number);
    return ids.length ? all.filter((r) => ids.includes(Number(r.id))) : all;
});

// The three count cards summarise every TRAIL the block is pointed
// at, which is why they are read before max_items caps the card list:
// a "12 open" that only counted the four cards on screen would be a
// different number for the same question. In networks mode they still
// count trails; see trailCounts.js.
const counts = computed(() => trailCounts(props.site_data, mode.value, props.config.item_ids || props.config.network_ids || []));
const countCards = computed(() => [
    { tone: 'open', label: 'Open', count: counts.value.open },
    { tone: 'caution', label: 'Caution', count: counts.value.caution },
    { tone: 'closed', label: 'Closed', count: counts.value.closed },
]);
// The summary row: a sentence, a bar split by count and a legend. A
// tone with nothing in it gets no segment and no legend entry, so the
// bar never shows a sliver for zero.
const countTotal = computed(() => counts.value.open + counts.value.caution + counts.value.closed);
const countParts = computed(() => countCards.value.filter((c) => c.count > 0));
// Caution is open trail, so it counts toward "X of Y trails open"
// (usable); "All trails open" is kept for when nothing is on caution,
// since the bar and legend beside it would say otherwise.
const summary = computed(() => {
    const { open, usable } = counts.value;
    const total = countTotal.value;
    if (!total) return '';
    if (open === total) return total === 1 ? 'The trail is open' : `All ${total} trails open`;
    return `${usable} of ${total} trails open`;
});

// The crew's note says what the trail is like; the wetness reading
// says how wet. A row shows whichever it has, both when it has both.
function conditionText(row) {
    return [wetnessLine(row), row?.note].filter(Boolean).join(' · ');
}

// 0 means every row.
const maxItems = computed(() => {
    const n = Math.floor(Number(props.config.max_items) || 0);
    return n > 0 ? n : 0;
});
const visibleRows = computed(() => (maxItems.value ? rows.value.slice(0, maxItems.value) : rows.value));

const marquee = computed(() => !!props.config.marquee);
// Each pill in the auto-scrolling strip opens its trail's or network's
// page: a strip that keeps moving is something a visitor reaches for.
const pillHref = (row) => conditionRowHref(props.site_data, mode.value, row);
const rowHref = (row) => pillHref(row) || props.config.cta_href || null;

// ── Marquee ─────────────────────────────────────────────────────────
// A strip that fits needs no animation, and a strip that animates when
// it fits reads as a bug. So the row is measured against its track and
// the loop only runs on a real overflow. The set is rendered twice so
// the seam passes off screen.
const stripEl = ref(null);
const trackEl = ref(null);
const marqueeRuns = ref(false);
const marqueeDuration = ref('40s');
function measure() {
    if (!marquee.value || !stripEl.value || !trackEl.value) {
        marqueeRuns.value = false;
        return;
    }
    // The set is doubled only WHILE the loop runs (the template's
    // v-if), so halve only then. Halving a single set made the strip run
    // only once it was twice the page's width: always in the builder's
    // narrow canvas, almost never on the live site.
    const trackWidth = trackEl.value.scrollWidth / (marqueeRuns.value ? 2 : 1);
    marqueeRuns.value = trackWidth > stripEl.value.clientWidth + 4;
    // About 40px a second, with a 40s floor so a short overflow does
    // not whip past.
    marqueeDuration.value = `${Math.max(40, Math.round(trackWidth / 40))}s`;
}
onMounted(() => {
    measure();
    // Web fonts change the strip's width after the first paint.
    document.fonts?.ready.then(measure);
    window.addEventListener('resize', measure);
});
onUnmounted(() => window.removeEventListener('resize', measure));
watch([marquee, () => visibleRows.value.length], () => nextTick(measure));

// ── Optional forecast ───────────────────────────────────────────────
// The Forecast block's own strip and settings (forecastSlots.js), set
// above or below the conditions, inside their white panel or on the
// block's background.
const fc = computed(() => conditionsForecast(props.config));
const fcSlots = computed(() => forecastSlots(fcData.value, fc.value.mode, fc.value.days));
const showForecast = computed(() => fc.value.show && fcSlots.value.length > 0);
// No rows and no forecast is nothing to report: the editor says so, a
// visitor gets no block at all rather than a heading over "No trail
// networks to show yet." (Mus, 2026-10-11).
const editing = inject('tiqEditorCanvas', false);
const hasContent = computed(() => Boolean(summary.value) || visibleRows.value.length > 0 || showForecast.value);
const hasPanel = computed(() => !!(summary.value || visibleRows.value.length));
const fcInside = computed(() => fc.value.panel === 'card' && hasPanel.value);
const fcOutsideVariant = computed(() => (fc.value.panel === 'card' ? 'card' : 'transparent'));
const fcData = computed(() => forecastFor(props.site_data, fc.value.network));
const fcUnits = computed(() => fcData.value?.unit_system || 'metric');
</script>

<template>
    <!-- Nothing at all when the organization does not publish trail
         status (see ConditionsBlock). -->
    <section v-if="site_data?.show_status !== false && (hasContent || editing)" :id="config.anchor_id || null" class="th-block th-sec th-conds" :style="styleVars(config)">
        <div class="th-wrap">
            <!-- Both headings are optional: left blank, the block is just
                 the box (and the link, if it has one). -->
            <div v-if="eyebrow || heading || config.cta_href" class="th-head">
                <div>
                    <p v-if="eyebrow" class="th-eyebrow">{{ eyebrow }}</p>
                    <h2 v-if="heading" class="th-h2">{{ heading }}</h2>
                </div>
                <a v-if="config.cta_href" :href="config.cta_href" class="th-more th-conds__more">
                    {{ config.cta_label || 'All trail conditions' }} →
                </a>
            </div>

            <ThConditionsForecast v-if="showForecast && fc.position === 'top' && !fcInside" :slots="fcSlots" :mode="fc.mode" :heading="fc.heading" :subtitle="fc.subtitle" :unit-system="fcUnits" :position="fc.position" :variant="fcOutsideVariant" />

            <div v-if="summary || visibleRows.length" class="th-conds__panel">
                <ThConditionsForecast v-if="showForecast && fc.position === 'top' && fcInside" :slots="fcSlots" :mode="fc.mode" :heading="fc.heading" :subtitle="fc.subtitle" :unit-system="fcUnits" :position="fc.position" variant="inside" />
                <div v-if="summary" class="th-conds__summary">
                    <div class="th-conds__summary-text">{{ summary }}</div>
                    <div class="th-conds__bar" aria-hidden="true">
                        <span v-for="c in countParts" :key="c.tone" class="th-conds__bar-part" :class="`th-dot--${c.tone}`" :style="{ flex: c.count }"></span>
                    </div>
                    <div class="th-conds__legend">
                        <span v-for="c in countParts" :key="c.tone" class="th-conds__legend-item">
                            <span class="th-dot" :class="`th-dot--${c.tone}`"></span>{{ c.count }} {{ c.label }}
                        </span>
                    </div>
                </div>

                <!-- Marquee: the same rows as a compact strip of pills. -->
                <div v-if="marquee && visibleRows.length"
                     ref="stripEl"
                     class="th-conds__strip"
                     :class="{ 'is-running': marqueeRuns }"
                     :style="{ '--th-marquee': marqueeDuration }">
                    <div ref="trackEl" class="th-conds__track" :class="{ 'is-running': marqueeRuns }">
                        <component :is="pillHref(row) ? 'a' : 'span'" v-for="row in visibleRows" :key="row.id"
                                   :href="pillHref(row) || undefined" class="th-pill"
                                   :class="[`th-pill--${statusTone(row.bucket)}`, { 'th-conds__pill--link': pillHref(row) }]">
                            <span class="th-dot"></span>{{ row.name }} · {{ statusLabel(row) }}
                        </component>
                        <!-- The duplicate the loop passes through. Hidden
                             from assistive tech so nothing is read twice. -->
                        <template v-if="marqueeRuns">
                            <component :is="pillHref(row) ? 'a' : 'span'" v-for="row in visibleRows" :key="`dup-${row.id}`"
                                       :href="pillHref(row) || undefined" :tabindex="pillHref(row) ? -1 : undefined" class="th-pill"
                                       :class="[`th-pill--${statusTone(row.bucket)}`, { 'th-conds__pill--link': pillHref(row) }]" aria-hidden="true">
                                <span class="th-dot"></span>{{ row.name }} · {{ statusLabel(row) }}
                            </component>
                        </template>
                    </div>
                </div>

                <div v-else-if="visibleRows.length" class="th-conds__rows">
                    <!-- A row opens its own trail's or network's page (Mus,
                         2026-10-12: every row used to open the block's
                         "All trail conditions" link). The block's link is
                         the fallback for a row with no page of its own. -->
                    <component :is="rowHref(row) ? 'a' : 'div'" v-for="row in visibleRows" :key="row.id"
                               :href="rowHref(row) || null" class="th-conds__row">
                        <span class="th-dot th-conds__row-dot" :class="`th-dot--${statusTone(row.bucket)}`"></span>
                        <span class="th-conds__row-main">
                            <span class="th-conds__name">{{ row.name }}</span>
                            <span v-if="conditionText(row)" class="th-conds__condition">{{ conditionText(row) }}</span>
                            <span v-if="distanceText(row)" class="th-mono th-conds__meta">{{ distanceText(row) }}</span>
                        </span>
                        <span class="th-conds__status" :class="`th-conds__status--${statusTone(row.bucket)}`">{{ statusLabel(row) }}</span>
                    </component>
                </div>
                <ThConditionsForecast v-if="showForecast && fc.position === 'bottom' && fcInside" :slots="fcSlots" :mode="fc.mode" :heading="fc.heading" :subtitle="fc.subtitle" :unit-system="fcUnits" :position="fc.position" variant="inside" />
            </div>

            <p v-else-if="editing" class="th-small th-conds__empty">
                {{ mode === 'trails' ? 'No trails to show yet.' : 'No trail networks to show yet.' }}
            </p>

            <ThConditionsForecast v-if="showForecast && fc.position === 'bottom' && !fcInside" :slots="fcSlots" :mode="fc.mode" :heading="fc.heading" :subtitle="fc.subtitle" :unit-system="fcUnits" :position="fc.position" :variant="fcOutsideVariant" />
        </div>
    </section>
</template>

<style scoped>
.th-conds__more { color: inherit; }
.th-conds__panel {
    background: #fff;
    border: 1px solid var(--th-line);
    border-radius: var(--r);
    overflow: hidden;
}
.th-conds__summary {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px 24px;
    padding: 16px 20px;
    border-bottom: 1px solid var(--th-line);
}
.th-conds__summary-text { font: 700 18px/1.2 var(--th-display); }
.th-conds__bar {
    flex: 1;
    min-width: 140px;
    height: 8px;
    display: flex;
    gap: 2px;
    border-radius: 999px;
    overflow: hidden;
}
.th-conds__bar-part { display: block; min-width: 4px; }
.th-conds__legend { display: flex; flex-wrap: wrap; gap: 6px 16px; }
.th-conds__legend-item {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font: 500 12px/1 var(--th-mono);
    letter-spacing: .06em;
    text-transform: uppercase;
    color: var(--th-ink);
}
.th-conds__legend-item .th-dot { width: 8px; height: 8px; }

/* Each row rules its right and bottom edge, and the grid is pulled
   1px past the panel's own edge so the outermost rules fall under the
   panel's border instead of doubling it. A gap over a coloured
   background would draw the same lines and paint an odd count's empty
   last cell grey. */
.th-conds__rows {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 360px), 1fr));
    margin: 0 -1px -1px 0;
}
.th-conds__row {
    background: #fff;
    border-right: 1px solid var(--th-line-soft);
    border-bottom: 1px solid var(--th-line-soft);
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 20px;
    min-height: 56px;
    color: inherit;
    text-decoration: none !important;
}
a.th-conds__row:hover, a.th-conds__row:focus-visible { background: var(--tint); }
.th-conds__row-dot { width: 9px; height: 9px; }
.th-conds__row-main { flex: 1; min-width: 0; display: flex; flex-wrap: wrap; align-items: baseline; gap: 2px 10px; }
.th-conds__name { font: 700 16px/1.25 var(--th-display); color: var(--text-heading, inherit); }
.th-conds__condition { font-size: 14px; color: var(--th-muted); }
.th-conds__meta { font-size: 11px; letter-spacing: .06em; text-transform: uppercase; color: var(--th-meta); }
.th-conds__status {
    font: 500 11px/1 var(--th-mono);
    letter-spacing: .06em;
    text-transform: uppercase;
    white-space: nowrap;
    color: #3b4a57;
}
.th-conds__status--open { color: #1d6b35; }
.th-conds__status--caution { color: #7d5200; }
.th-conds__status--closed { color: #9b2a1f; }
.th-conds__empty { font-style: italic; }

.th-conds__strip { overflow: hidden; padding: 14px 20px; }
.th-conds__strip.is-running {
    mask-image: linear-gradient(to right, transparent 0, #000 4%, #000 96%, transparent 100%);
    -webkit-mask-image: linear-gradient(to right, transparent 0, #000 4%, #000 96%, transparent 100%);
}
.th-conds__track { display: flex; gap: 10px; flex-wrap: nowrap; width: max-content; }
.th-conds__track.is-running {
    animation: thCondMarquee var(--th-marquee, 40s) linear infinite;
    will-change: transform;
}
.th-conds__track.is-running:hover,
.th-conds__track.is-running:focus-within { animation-play-state: paused; }
.th-conds__pill--link { text-decoration: none; transition: box-shadow .15s; }
.th-conds__pill--link:hover { text-decoration: none; box-shadow: inset 0 0 0 1px currentColor; }
.th-conds__pill--link:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
@keyframes thCondMarquee {
    from { transform: translateX(0); }
    to   { transform: translateX(-50%); }
}
@media (prefers-reduced-motion: reduce) {
    .th-conds__track.is-running { animation: none; }
}

</style>
